import { analisarSistemaVM, type ResultadoSistemaVM } from './vantagemMecanica'
import { localizarPontoNoMundo } from './ancoragem'
import { parametrosDoObjeto } from './parametros'
import { terrenoNoPonto } from './superficies'
import { folgaPorTensao, folgasPorConservacao, tensaoDaCordaFrouxaKgf, vaosDaCorda } from './catenariaCorda'
import type { CenaTabletop, ObjetoTabletop, VetorTabletop } from './tipos'

type V = VetorTabletop

const sub = (a: V, b: V): V => [a[0] - b[0], a[1] - b[1], a[2] - b[2]]
const soma = (a: V, b: V): V => [a[0] + b[0], a[1] + b[1], a[2] + b[2]]
const esc = (a: V, k: number): V => [a[0] * k, a[1] * k, a[2] * k]
const comp = (a: V) => Math.hypot(a[0], a[1], a[2])
const unit = (a: V): V => {
  const n = comp(a)
  return n > 1e-9 ? [a[0] / n, a[1] / n, a[2] / n] : [0, 1, 0]
}

/** Distância, antes do fim da rota (polia/ponto mais alto), em que a maca para (m). */
const PARADA_ANTES_DO_FIM_M = 0.75

/** Objetos que não saem do lugar quando a corda é puxada: servem de ponto fixo do sistema de redução. */
const TIPOS_PONTO_FIXO: ReadonlySet<string> = new Set(['toco', 'arvore', 'poste', 'placaAncoragem', 'pontoAncoragem', 'envolvimento', 'caminhaoBombeiros', 'estaca'])

/**
 * O sistema de redução precisa de um ponto fixo: uma fita ou corda que liga a sua
 * ancoragem a um toco, árvore, poste, ponto de ancoragem, estaca no solo ou à viatura (roda).
 * Devolve o id do objeto fixo, ou `null` se o sistema está solto.
 */
export function pontoFixoDoSistema(cena: CenaTabletop, idSistema: string): string | null {
  const objetos = cena.objetos ?? []
  for (const corda of cena.cordas ?? []) {
    const noSistema = corda.percurso.find((p) => p.idObjeto === idSistema && p.idPonto === 'ancoragem')
    if (!noSistema) continue
    for (const p of corda.percurso) {
      if (p.idObjeto === idSistema) continue
      const alvo = objetos.find((o) => o.id === p.idObjeto)
      if (alvo && (TIPOS_PONTO_FIXO.has(alvo.tipo) || alvo.veiculo)) return alvo.id
    }
  }
  return null
}

export interface CadeiaIcamento {
  readonly valido: boolean
  readonly motivoInvalido?: string
  readonly idMaca?: string
  readonly idSistemaVM?: string
  /** O toco, árvore, poste, estaca ou viatura em que o sistema de redução está preso. */
  readonly idPontoFixo?: string
  readonly idCordaTracao?: string
  readonly idCarrinho?: string
  readonly idsPecasIçadas?: readonly string[]
  readonly posicaoInicialMaca?: V
  readonly deslocamentoMaca?: V
  readonly cursoInicialM?: number
  /** Percurso completo da maca, do ponto onde está até o fim da rota (m). */
  readonly percursoTotalM?: number
  /** Quantas puxadas completas do sistema de redução o percurso exige (a cada uma, o bloco reinicia). */
  readonly ciclos?: number
  /** Duração sugerida da animação, proporcional ao percurso (s). */
  readonly duracaoSugeridaS?: number
  readonly razao: number
  readonly cargaKgf: number
  readonly analiseVM?: ResultadoSistemaVM
}

/**
 * Identifica a cadeia de içamento na cena:
 * Procura pela maca ou animal (cavalo/vaca), pela corda de içamento/tração,
 * pelo sistema de vantagem mecânica ou polias, e calcula os vetores de deslocamento para a animação do teste.
 */
export function identificarSistemaIcamento(cena: CenaTabletop): CadeiaIcamento {
  const objetos = cena?.objetos ?? []
  const cordas = cena?.cordas ?? []

  // 1. Procurar a carga de resgate (maca, cavalo ou vaca)
  // Se houver corda conectada a um deles, prioriza o conectado
  const candidatosCarga = objetos.filter((o) => o.tipo === 'maca' || o.tipo === 'cavalo' || o.tipo === 'vaca')
  const cargaAlvo =
    candidatosCarga.find((c) => cordas.some((corda) => corda.percurso.some((p) => p.idObjeto === c.id))) ??
    candidatosCarga[0]

  if (!cargaAlvo) {
    return {
      valido: false,
      motivoInvalido: 'Nenhuma maca de resgate encontrada (nem animal como cavalo ou vaca) na cena para içar.',
      razao: 1,
      cargaKgf: 120,
    }
  }

  const maca = cargaAlvo
  const nomeCarga = maca.tipo === 'cavalo' ? 'O cavalo' : maca.tipo === 'vaca' ? 'A vaca' : 'A maca'
  const cargaKgf =
    maca.tipo === 'cavalo' || maca.tipo === 'vaca'
      ? (parametrosDoObjeto(maca).pesoKg ?? 500)
      : 120

  // 2. Procurar sistema de redução (sistemaVM) se houver
  const sistemaVM = objetos.find((o) => o.tipo === 'sistemaVM')

  if (sistemaVM) {
    const fixo = pontoFixoDoSistema(cena, sistemaVM.id)
    if (!fixo) {
      return {
        valido: false,
        motivoInvalido:
          'O sistema de redução está sem ponto fixo. Prenda a ancoragem dele (fita) a um toco, árvore, poste, estaca no chão ou à roda da viatura.',
        idMaca: maca.id,
        idSistemaVM: sistemaVM.id,
        razao: 1,
        cargaKgf,
      }
    }
  }

  // 3. Procurar a corda de tração/içamento
  let cordaTracao = cordas.find((c) => c.funcao === 'tracao' || c.sentido !== undefined)

  // Se não achou por função ou sentido, procura corda que conecte na carga ou no sistema
  if (!cordaTracao) {
    cordaTracao = cordas.find(
      (c) =>
        c.percurso.some((p) => p.idObjeto === maca.id) ||
        (sistemaVM && c.percurso.some((p) => p.idObjeto === sistemaVM.id)),
    )
  }

  if (!cordaTracao) {
    return {
      valido: false,
      motivoInvalido: `${nomeCarga} não está conectada a nenhuma corda de içamento ou tração.`,
      idMaca: maca.id,
      razao: 1,
      cargaKgf,
    }
  }

  // 4. Verificar se há carrinho de tirolesa (polia que corre em tirolesa)
  const carrinho = cena.objetos.find(
    (o) =>
      o.tipo === 'polia' &&
      cordas.some((c) => c.funcao === 'tirolesa' && c.percurso.some((p) => p.idObjeto === o.id)),
  )

  // 5. Parâmetros de redução
  const paramsVM = sistemaVM ? parametrosDoObjeto(sistemaVM) : null
  const razao = paramsVM?.razao ?? 1
  const cursoInicialM = paramsVM?.cursoM ?? 2.4
  const analiseVM = sistemaVM ? analisarSistemaVM({ razao, cargaKgf, fusivel: paramsVM?.fusivel }) : undefined

  const posMaca = maca.transformacao.posicao

  // 6a. Identificar peças que sobem juntas (mosquetão da maca, nó oito logo acima)
  const idsPecasIçadas: string[] = [maca.id]
  if (carrinho) idsPecasIçadas.push(carrinho.id)

  for (const obj of cena.objetos) {
    if (obj.tipo === 'mosquetao' || obj.tipo === 'noOito') {
      const p = obj.transformacao.posicao
      // Se estiver até 60 cm da maca ou do carrinho, sobe junto
      const dMaca = Math.hypot(p[0] - posMaca[0], p[2] - posMaca[2])
      const dyMaca = p[1] - posMaca[1]
      if (dMaca < 0.5 && dyMaca > 0 && dyMaca < 1.2) {
        idsPecasIçadas.push(obj.id)
      } else if (carrinho) {
        const pCar = carrinho.transformacao.posicao
        if (Math.hypot(p[0] - pCar[0], p[1] - pCar[1], p[2] - pCar[2]) < 0.6) {
          idsPecasIçadas.push(obj.id)
        }
      }
    }
  }

  // 6b. Determinar o percurso COMPLETO da maca: até o fim da rota, e não só um curso do sistema
  let deslocamento: V = [0, 1.8, 0] // sem rota identificável: sobe 1,8 m

  if (carrinho) {
    // Tirolesa invertida: o carrinho sobe pela própria tirolesa, rumo ao ponto mais alto dela
    const tirolesa = cordas.find((c) => c.funcao === 'tirolesa' && c.percurso.some((p) => p.idObjeto === carrinho.id))
    let alvo: V | null = null
    for (const ref of tirolesa?.percurso ?? []) {
      const p = localizarPontoNoMundo(cena, ref)
      if (p && (!alvo || p[1] > alvo[1])) alvo = p
    }
    const posCarrinho = carrinho.transformacao.posicao
    if (alvo && alvo[1] > posCarrinho[1] + 0.3) {
      const dist = Math.max(0.5, comp(sub(alvo, posCarrinho)) - PARADA_ANTES_DO_FIM_M)
      deslocamento = esc(unit(sub(alvo, posCarrinho)), dist)
    }
  } else {
    // Ponto mais alto do percurso da corda de tração
    // (ignora as peças que sobem junto: o limite é a polia fixa, não o que viaja)
    const viajantes = new Set(idsPecasIçadas)
    let pontoMaisAlto: V | null = null
    let topoDosViajantesY = posMaca[1]
    for (const ref of cordaTracao.percurso) {
      const p = localizarPontoNoMundo(cena, ref)
      if (!p) continue
      if (viajantes.has(ref.idObjeto)) topoDosViajantesY = Math.max(topoDosViajantesY, p[1])
      else if (!pontoMaisAlto || p[1] > pontoMaisAlto[1]) pontoMaisAlto = p
    }
    const subidaVertical = pontoMaisAlto ? pontoMaisAlto[1] - posMaca[1] : 0
    const afastamento = pontoMaisAlto ? Math.hypot(pontoMaisAlto[0] - posMaca[0], pontoMaisAlto[2] - posMaca[2]) : 0

    if (pontoMaisAlto && subidaVertical > 0.8 && pontoMaisAlto[1] - topoDosViajantesY > 0.8 && afastamento <= subidaVertical) {
      // Içamento vertical: sobe até parar a PARADA_ANTES_DO_FIM_M da polia mais alta
      deslocamento = [0, Math.max(0.8, pontoMaisAlto[1] - topoDosViajantesY - PARADA_ANTES_DO_FIM_M), 0]
    } else {
      // Arrasto/puxada pelo terreno: a maca anda rumo ao ponto da corda mais próximo dela
      const idxMaca = cordaTracao.percurso.findIndex((p) => p.idObjeto === maca.id)
      const vizinho = idxMaca > 0 ? cordaTracao.percurso[idxMaca - 1] : cordaTracao.percurso[idxMaca + 1]
      const alvo = vizinho ? localizarPontoNoMundo(cena, vizinho) : null
      if (alvo) {
        const folga = posMaca[1] - terrenoNoPonto(cena, posMaca[0], posMaca[2]).altura
        const alvoNoChao: V = [alvo[0], terrenoNoPonto(cena, alvo[0], alvo[2]).altura + folga, alvo[2]]
        const dist = Math.max(0.5, comp(sub(alvoNoChao, posMaca)) - PARADA_ANTES_DO_FIM_M)
        deslocamento = esc(unit(sub(alvoNoChao, posMaca)), dist)
      }
    }
  }

  const percursoTotalM = comp(deslocamento)
  // cada puxada do sistema de redução recolhe até `cursoInicialM` de içamento; o resto vira novas puxadas
  const ciclos = sistemaVM ? Math.max(1, Math.ceil(percursoTotalM / Math.max(0.5, cursoInicialM))) : 1
  const duracaoSugeridaS = Math.min(14, Math.max(3.5, 1.1 * percursoTotalM + (ciclos - 1) * 0.4))

  return {
    valido: true,
    idMaca: maca.id,
    idSistemaVM: sistemaVM?.id,
    idPontoFixo: sistemaVM ? pontoFixoDoSistema(cena, sistemaVM.id) ?? undefined : undefined,
    idCordaTracao: cordaTracao.id,
    idCarrinho: carrinho?.id,
    idsPecasIçadas,
    posicaoInicialMaca: posMaca,
    deslocamentoMaca: deslocamento,
    cursoInicialM,
    percursoTotalM,
    ciclos,
    duracaoSugeridaS,
    razao,
    cargaKgf,
    analiseVM,
  }
}

/**
 * Calcula o quadro de animação da cena para um dado progresso (0 a 1):
 * - Move a maca e seus acessórios proporcionalmente a `progresso * deslocamentoMaca`.
 * - Mantém o sistema de redução (`sistemaVM`) parado no ponto fixo.
 */
export function calcularQuadroIcamento(
  cenaOriginal: CenaTabletop,
  cadeia: CadeiaIcamento,
  progresso: number,
): CenaTabletop {
  if (!cadeia.valido || !cadeia.deslocamentoMaca) return cenaOriginal

  const t = Math.max(0, Math.min(1, progresso))
  const delta = esc(cadeia.deslocamentoMaca, t)
  const pecasIçadas = new Set(cadeia.idsPecasIçadas ?? [])

  const novosObjetos = cenaOriginal.objetos.map((obj) => {
    // 1. Peças que sobem com a maca
    if (pecasIçadas.has(obj.id)) {
      return {
        ...obj,
        transformacao: {
          ...obj.transformacao,
          posicao: soma(obj.transformacao.posicao, delta),
        },
      }
    }

    // 2. O sistema de redução NÃO se move: está preso a um ponto fixo. Quem anda é a maca; a corda de
    // tração corre pelo sistema, e as puxadas (`ciclos`) são só a contagem de quantas vezes o operador repete.
    return obj
  })

  const cenaComObjetos: CenaTabletop = { ...cenaOriginal, objetos: novosObjetos }
  if (!cenaOriginal.cordas?.length) return cenaComObjetos

  // catenária: a corda de comprimento fixo pende quando as pontas se aproximam; a que leva pouca tensão pende pelo próprio peso
  const novasCordas = cenaOriginal.cordas.map((corda) => {
    // a de tração está esticada pela carga; a fita abraça a estrutura; a tirolesa é esticada e o carrinho corre sobre ela
    if (corda.id === cadeia.idCordaTracao || corda.funcao === 'tracao' || corda.funcao === 'fita' || corda.funcao === 'tirolesa') return corda
    const porConservacao = t > 0 ? folgasPorConservacao(cenaOriginal, cenaComObjetos, corda) : (corda.folgas ?? [])
    const tensao = tensaoDaCordaFrouxaKgf(corda.funcao)
    const vaos = tensao === null ? [] : vaosDaCorda(cenaComObjetos, corda)
    const folgas = corda.percurso.slice(1).map((_, i) => {
      const base = porConservacao[i] ?? 0
      const vao = vaos[i]
      // a corda frouxa nunca fica mais reta que o próprio peso permite
      return vao && tensao !== null ? Math.max(base, folgaPorTensao(vao, tensao)) : base
    })
    const igual = folgas.every((f, i) => Math.abs(f - (corda.folgas?.[i] ?? 0)) < 1e-6)
    if (igual) return corda
    while (folgas.length > 0 && folgas[folgas.length - 1] === 0) folgas.pop()
    const { folgas: _antigas, ...semFolgas } = corda
    return folgas.length > 0 ? { ...semFolgas, folgas } : semFolgas
  })

  return { ...cenaComObjetos, cordas: novasCordas }
}
